import { z } from 'zod';
import { TenantRegionSchema } from './tenantRegion';
import { computeCapabilities } from './billing';
import { CrmPartnerCommercialModelSchema } from './crmPartner';
import { SplitGatewaySchema } from './salesSplit';
import { partnerItemPaidThrough } from './partnerRemittance';
import { CommercialMode, PricingSource, Sku, SubscriptionItemOrigin, type BillingCycle, type Currency } from './enums';
import {
  BillingCycleSchema,
  CountrySchema,
  CurrencySchema,
  PaymentProviderSchema,
  PhoneE164Schema,
  SkuSchema,
  TenantKindSchema,
  TenantLocaleSchema,
  type SubscriptionItem,
} from './schemas';

/**
 * Venda assistida pelo CRM (#679 F1, #700): o contrato compartilhado entre o
 * CRM web (que monta a composição e antecipa as recusas), as callables do
 * codebase principal (que decidem) e o CRM functions (que reage ao pagamento).
 *
 * Tudo aqui é puro. As funções de checagem existem uma vez só para que a tela
 * da F2 recuse exatamente o que o servidor recusaria — a UI antecipa, o
 * servidor decide (9.1).
 */

/** Um item da composição: SKU e preço negociado, em cents inteiros. */
export const SaleItemSchema = z.object({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
});
export type SaleItemInput = z.infer<typeof SaleItemSchema>;

/**
 * Ciclos que a venda assistida pode vender HOJE (#679 D13, §14.4-2). Fonte
 * única: o servidor (`assertSaleIsSellable`) recusa o resto, e a tela
 * Contratação (F2) só oferece estes.
 *
 * Trimestral e semestral existem no schema e no cálculo, mas só podem ser
 * vendidos depois que os apps que os entendem estiverem publicados e o
 * `minBuild` apertado: o Android publicado mostra o total trimestral "por
 * mês" e o iOS publicado mostra o valor cru.
 *
 * Abrir = acrescentar o ciclo aqui, no mesmo commit que confirma a publicação.
 *
 * Histórico:
 *  - 2026-09-29 23:01 +0200 — (#737, #776) trimestral e semestral abertos, a
 *    pedido do owner. Pré-condições, NESTA ordem, antes de publicar: iOS com
 *    `3dc1d7dc6` (build ≥ 34) aprovado na App Store; `minBuild` de
 *    `/appConfig/android` ≥ 243 e `/appConfig/ios` ≥ 34; pisos trimestral e
 *    semestral preenchidos em `crm_appConfig/pricingFloors` (sem piso, a venda
 *    é recusada com `NO_FLOOR`). A trava `cycle_not_yet_sellable` do servidor
 *    fica — hoje inalcançável, volta a valer se um ciclo novo entrar no enum.
 */
export const SELLABLE_SALE_CYCLES: readonly BillingCycle[] = ['monthly', 'quarterly', 'semiannual', 'yearly'];

// ---------------------------------------------------------------------------
// Piso de preço — `crm_appConfig/pricingFloors` (9.1, D12, D25)
// ---------------------------------------------------------------------------

/** Doc id do piso em `crm_appConfig`. Escrito pela F2 (`crmUpdatePricingFloors`). */
export const CRM_PRICING_FLOORS_DOC_ID = 'pricingFloors';

const CentsSchema = z.number().int().nonnegative();

/**
 * Piso de um SKU numa moeda e num ciclo.
 *
 * `list_cents` é LEGADO (#964, owner 2026-10-13: "não vamos mais usar valor
 * Lista"). Era o preço de lista exibido como referência (9.17, #701); nenhum
 * código o lê mais, e a escrita (`CrmUpdatePricingFloorsInput`) já não o aceita.
 * Fica opcional aqui só para o documento gravado antes continuar passando no
 * parse — o primeiro save da tela de piso o apaga (o save substitui o doc).
 */
export const PricingFloorEntrySchema = z.object({
  min_cents: CentsSchema,
  list_cents: CentsSchema.optional(),
});
export type PricingFloorEntry = z.infer<typeof PricingFloorEntrySchema>;

/**
 * `crm_appConfig/pricingFloors`.
 *
 * `items[sku][currency][cycle]` e `package[currency][cycle]`. O texto da D12
 * punha os SKUs no topo do documento, ao lado de `package`; aqui os SKUs moram
 * em `items`, para um SKU nunca disputar a chave com um campo do documento.
 *
 * Chaves como `string` e não como enum de propósito: `z.record` com chave enum
 * exige TODAS as chaves no Zod 4, e um piso é esparso por natureza — um SKU
 * pode ter piso em BRL e não em EUR. A ausência é informação (ver
 * `checkSalePrices`), não erro de schema.
 *
 * Piso por ciclo é independente: o mensal × 12 **não** é o anual (D12).
 */
export const PricingFloorsSchema = z.object({
  items: z
    .record(z.string(), z.record(z.string(), z.record(z.string(), PricingFloorEntrySchema)))
    .default({}),
  package: z
    .record(z.string(), z.record(z.string(), z.object({ min_cents: CentsSchema })))
    .default({}),
  /**
   * #965 — preço FIXO da licença que o parceiro `RESELLER` paga, por SKU e
   * moeda, em centavos por MÊS: `partner_license[sku][currency]`. Só mensal
   * (decisão do owner, 2026-10-13: o modelo vende só mensal).
   *
   * Separado de `items` de propósito: não é piso (o parceiro não negocia, e o
   * valor pode ficar ABAIXO do piso da Contratação — R$250 contra um piso de
   * R$300, por exemplo), e uma célula de piso sem `min_cents` quebraria a
   * leitura fail-closed de `checkSalePrices`. Ausente = SKU que o RESELLER não
   * compra naquela moeda (`checkPartnerLicensePrices`, `NO_LICENSE_PRICE`).
   * `0` é preço configurado (como o piso do `MOD_PRINT_TSPL`, D25).
   *
   * Opcional: doc anterior à #965 lê sem licença nenhuma — o RESELLER não
   * vende até o ADMIN preencher a coluna. Falha fechada. (Opcional e não
   * `.default({})` para o tipo de saída continuar aceitando os `PricingFloors`
   * literais que os testes do CRM montam sem a licença.)
   */
  partner_license: z.record(z.string(), z.record(z.string(), CentsSchema)).optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type PricingFloors = z.infer<typeof PricingFloorsSchema>;

/**
 * Doc id da licença do parceiro RESELLER em `crm_appConfig` (#967, M9).
 *
 * Origem: #967 — 2026-10-13 22:10 +0200. A licença morava em
 * `pricingFloors.partner_license`, que TODO parceiro ativo lê pelas rules (a
 * Contratação do DIRECT cumpre o piso) — o parceiro DIRECT via o preço de
 * atacado do RESELLER, que pode ficar abaixo do piso. Num doc próprio, a rule
 * libera só ao time do CRM e ao parceiro RESELLER.
 *
 * Usado por: `readPricingFloors` (Functions, `assistedSaleGuards.ts`),
 * `crmUpdatePricingFloors` (crm-functions, escreve), `usePricingFloors` (CRM).
 */
export const CRM_PARTNER_LICENSE_DOC_ID = 'partnerLicense';

/**
 * `crm_appConfig/partnerLicense` — preço FIXO mensal da licença do parceiro
 * RESELLER, por SKU e moeda, em CENTAVOS (mesmo formato de
 * `PricingFloorsSchema.partner_license`, que vira só o formato em memória).
 *
 * Dados: `{ "prices": { "BASE": { "BRL": 25000 }, "MOD_SCALE": { "BRL": 1500 } },
 * "updatedAt": 1790000000000, "updatedBy": "uid-admin" }`. Doc ausente = sem
 * licença (o RESELLER não vende — fail-closed). Escrito só por
 * `crmUpdatePricingFloors` (Admin SDK).
 *
 * Origem: #967 (M9) — 2026-10-13 22:10 +0200.
 */
export const PartnerLicenseDocSchema = z.object({
  prices: z.record(z.string(), z.record(z.string(), CentsSchema)).default({}),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
});
export type PartnerLicenseDoc = z.infer<typeof PartnerLicenseDocSchema>;

/**
 * Por que um preço foi recusado. União discriminada, e não string: a tela da
 * F2 mostra mensagem diferente para "abaixo do piso" (o colaborador sobe o
 * preço) e "sem piso" (o ADMIN precisa configurar — o colaborador não resolve).
 */
export type SalePriceViolation =
  | { readonly kind: 'NO_FLOOR'; readonly sku: string }
  | {
      readonly kind: 'BELOW_FLOOR';
      readonly sku: string;
      readonly minCents: number;
      readonly amountCents: number;
    }
  | { readonly kind: 'NO_PACKAGE_FLOOR' }
  | {
      readonly kind: 'PACKAGE_BELOW_FLOOR';
      readonly minCents: number;
      readonly totalCents: number;
    };

/**
 * Checa cada item e o pacote contra o piso. **Fail-closed**: SKU sem piso
 * configurado na moeda e no ciclo é item não vendável, e pacote sem piso é
 * venda não vendável — senão o piso vira decoração no dia em que alguém
 * esquecer de configurá-lo (9.1).
 *
 * A checagem é de **presença** (`typeof === 'number'`), nunca de verdade:
 * `min_cents: 0` é piso configurado — o `MOD_PRINT_TSPL` vale a partir de zero
 * (D25) — e ausente é piso inexistente. Os dois são falsy, e um `if (!min)`
 * trataria o TSPL como não vendável.
 */
export function checkSalePrices(
  floors: PricingFloors,
  sale: {
    readonly currency: Currency;
    readonly cycle: BillingCycle;
    readonly items: readonly SaleItemInput[];
  },
): SalePriceViolation[] {
  const violations: SalePriceViolation[] = [];
  for (const item of sale.items) {
    const min = floors.items[item.sku]?.[sale.currency]?.[sale.cycle]?.min_cents;
    if (typeof min !== 'number') {
      violations.push({ kind: 'NO_FLOOR', sku: item.sku });
      continue;
    }
    if (item.amountCents < min) {
      violations.push({
        kind: 'BELOW_FLOOR',
        sku: item.sku,
        minCents: min,
        amountCents: item.amountCents,
      });
    }
  }
  const packageMin = floors.package[sale.currency]?.[sale.cycle]?.min_cents;
  const total = sale.items.reduce((sum, it) => sum + it.amountCents, 0);
  if (typeof packageMin !== 'number') {
    violations.push({ kind: 'NO_PACKAGE_FLOOR' });
  } else if (total < packageMin) {
    violations.push({ kind: 'PACKAGE_BELOW_FLOOR', minCents: packageMin, totalCents: total });
  }
  return violations;
}

/**
 * Por que a composição de um parceiro `RESELLER` foi recusada (#965).
 * `NO_LICENSE_PRICE`: o ADMIN não configurou a licença do SKU na moeda — o
 * parceiro não resolve. `LICENSE_PRICE_MISMATCH`: o preço enviado não é o da
 * licença (aba desatualizada, ou alguém tentando outro preço).
 */
export type PartnerLicenseViolation =
  | { readonly kind: 'NO_LICENSE_PRICE'; readonly sku: string }
  | {
      readonly kind: 'LICENSE_PRICE_MISMATCH';
      readonly sku: string;
      readonly licenseCents: number;
      readonly amountCents: number;
    };

/**
 * O preço mensal da licença de um SKU para o parceiro `RESELLER`, ou `null`
 * quando não há preço configurado (o SKU não vende nesse modelo).
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Contrato: checagem de PRESENÇA (`typeof === 'number'`), como o piso — `0` é
 * preço configurado.
 *
 * Usado por: `checkPartnerLicensePrices` (este arquivo); CRM
 * `LeadCheckoutPage` (preço só leitura da composição).
 */
export function partnerLicensePriceFor(
  floors: Pick<PricingFloors, 'partner_license'>,
  sku: string,
  currency: Currency,
): number | null {
  const cents = floors.partner_license?.[sku]?.[currency];
  return typeof cents === 'number' ? cents : null;
}

/**
 * Confere uma venda de parceiro `RESELLER` contra a licença: cada item tem de
 * ter preço configurado e ser vendido EXATAMENTE por ele.
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Por quê: no RESELLER o preço é fixo e só leitura para o parceiro (owner,
 * 2026-10-13). A tela já manda o preço da licença; o servidor confere em vez
 * de confiar, e recusa a divergência em vez de corrigi-la em silêncio — uma
 * aba com preço velho tem de saber que o preço mudou antes de gerar o link.
 * Substitui `checkSalePrices` nessa venda: o piso não se aplica.
 *
 * Contrato: `[]` = ok. Só mensal — o ciclo é conferido por quem chama.
 *
 * Usado por: Functions `assertSaleIsSellable` (`lib/billing/assistedSaleGuards.ts`),
 * no `provisionTenantForSale` e no `createSaleCheckoutLink`.
 */
export function checkPartnerLicensePrices(
  floors: Pick<PricingFloors, 'partner_license'>,
  sale: { readonly currency: Currency; readonly items: readonly SaleItemInput[] },
): PartnerLicenseViolation[] {
  const violations: PartnerLicenseViolation[] = [];
  for (const item of sale.items) {
    const license = partnerLicensePriceFor(floors, item.sku, sale.currency);
    if (license === null) {
      violations.push({ kind: 'NO_LICENSE_PRICE', sku: item.sku });
    } else if (item.amountCents !== license) {
      violations.push({ kind: 'LICENSE_PRICE_MISMATCH', sku: item.sku, licenseCents: license, amountCents: item.amountCents });
    }
  }
  return violations;
}

/**
 * O piso vigente, só nas entradas que esta venda usou — o retrato gravado no
 * `checkoutAttempt` e no audit (9.1), para que uma mudança de piso depois não
 * reescreva a história de uma venda já feita.
 */
export function pricingFloorSnapshot(
  floors: PricingFloors,
  sale: {
    readonly currency: Currency;
    readonly cycle: BillingCycle;
    readonly items: readonly SaleItemInput[];
  },
): { items: Record<string, number | null>; package: number | null } {
  const items: Record<string, number | null> = {};
  for (const item of sale.items) {
    const min = floors.items[item.sku]?.[sale.currency]?.[sale.cycle]?.min_cents;
    items[item.sku] = typeof min === 'number' ? min : null;
  }
  const packageMin = floors.package[sale.currency]?.[sale.cycle]?.min_cents;
  return { items, package: typeof packageMin === 'number' ? packageMin : null };
}

// ---------------------------------------------------------------------------
// Forma da composição
// ---------------------------------------------------------------------------

export type SaleCompositionViolation =
  | { readonly kind: 'EMPTY' }
  | { readonly kind: 'BASE_NOT_FIRST' }
  | { readonly kind: 'DUPLICATE_SKU'; readonly sku: string };

/**
 * A forma da composição, antes de qualquer preço: o BASE é obrigatório e vem
 * primeiro (a Stripe mostra as linhas nessa ordem, e o reconciliador deriva o
 * ciclo pelo BASE), e nenhum SKU se repete — dois itens do mesmo módulo são a
 * cobrança dobrada da #410, só que nascida no link.
 */
export function checkSaleComposition(items: readonly SaleItemInput[]): SaleCompositionViolation[] {
  if (items.length === 0) return [{ kind: 'EMPTY' }];
  const violations: SaleCompositionViolation[] = [];
  if (items[0]?.sku !== Sku.BASE) violations.push({ kind: 'BASE_NOT_FIRST' });
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item.sku)) violations.push({ kind: 'DUPLICATE_SKU', sku: item.sku });
    seen.add(item.sku);
  }
  return violations;
}

// ---------------------------------------------------------------------------
// Inputs das callables
// ---------------------------------------------------------------------------

/** O lead do CRM de onde a venda sai. */
const SaleLeadRefSchema = z.object({
  boardId: z.string().min(1).max(128),
  leadId: z.string().min(1).max(128),
});

/**
 * `provisionTenantForSale` (#679 §4.3-2): cria o estabelecimento, o OWNER e a
 * assinatura sem gateway, a partir de uma venda. A composição vem junto porque
 * o piso é validado aqui também (D12), e porque a assinatura nasce com o valor
 * total negociado.
 */
export const ProvisionTenantForSaleInput = SaleLeadRefSchema.extend({
  company: z.object({
    name: z.string().trim().min(2).max(120),
    kind: TenantKindSchema,
    countryCode: CountrySchema,
    taxId: z.string().min(3).max(40),
    address: z.string().max(300).default(''),
    timeZone: z.string().min(1),
    /** 9.25 — o idioma do tenant; o CRM pré-preenche com o de quem cria. */
    locale: TenantLocaleSchema,
  }),
  owner: z.object({
    displayName: z.string().trim().min(2).max(120),
    email: z.email().max(254),
    phoneE164: PhoneE164Schema,
  }),
  billingCycle: BillingCycleSchema,
  items: z.array(SaleItemSchema).min(1).max(30),
});
export type ProvisionTenantForSaleInput = z.infer<typeof ProvisionTenantForSaleInput>;

/**
 * Item vendido por **parceiro** (#743, F8 da #679): além do que ele cobra do
 * cliente (`amountCents`, sobre o qual o piso D12 incide), o que ele nos
 * repassa por ciclo (`remitCents`).
 *
 * Repasse por item ≥ 0, e o do pacote > 0 (D12: "remit livre acima de zero").
 * Por item e não estrito porque o `MOD_PRINT_TSPL` tem piso 0 (D25): exigir
 * repasse positivo num item que pode ser vendido a 0 obrigaria o parceiro a
 * pagar para dar o módulo que nós damos de graça.
 */
export const PartnerSaleItemSchema = SaleItemSchema.extend({
  remitCents: z.number().int().nonnegative(),
});
export type PartnerSaleItemInput = z.infer<typeof PartnerSaleItemSchema>;

/**
 * `activatePartnerSale` (#743): o parceiro ativa o cliente dele. Mesmo cadastro
 * de `provisionTenantForSale`, mas sem link — nós não cobramos esse tenant, e
 * ele nasce liberado (`commercial_mode: PARTNER`, o gate ignora gateway).
 */
export const ActivatePartnerSaleInput = ProvisionTenantForSaleInput.extend({
  items: z
    .array(PartnerSaleItemSchema)
    .min(1)
    .max(30)
    .refine((items) => items.reduce((sum, it) => sum + it.remitCents, 0) > 0, {
      message: 'o repasse do pacote tem de ser maior que zero',
    }),
});
export type ActivatePartnerSaleInput = z.infer<typeof ActivatePartnerSaleInput>;

export const ActivatePartnerSaleOutput = z.object({
  tenantId: z.string(),
  ownerUid: z.string(),
  ownerAccountExisted: z.boolean(),
  /** O e-mail com o link de senha saiu agora (não há pagamento a esperar). */
  welcomeEmailSent: z.boolean(),  /**
   * #1078 (F2 da #960) — região onde o tenant vive. Opcional: servidor antigo não a devolve e o
   * cliente trata ausente como `southamerica-east1`. Os clientes passam a usá-la na F5 para abrir
   * o banco/Functions do tenant sem esperar o diretório (#1081-#1083).
   */
  region: TenantRegionSchema.optional(),
});
export type ActivatePartnerSaleOutput = z.infer<typeof ActivatePartnerSaleOutput>;

export const ProvisionTenantForSaleOutput = z.object({
  tenantId: z.string(),
  ownerUid: z.string(),
  /** A conta Auth do OWNER já existia (9.6) — a UI avisa o colaborador. */
  ownerAccountExisted: z.boolean(),  /**
   * #1078 (F2 da #960) — região onde o tenant vive. Opcional: servidor antigo não a devolve e o
   * cliente trata ausente como `southamerica-east1`. Os clientes passam a usá-la na F5 para abrir
   * o banco/Functions do tenant sem esperar o diretório (#1081-#1083).
   */
  region: TenantRegionSchema.optional(),
});
export type ProvisionTenantForSaleOutput = z.infer<typeof ProvisionTenantForSaleOutput>;

/**
 * `createSaleCheckoutLink` (#679 §4.3-3): o link que autoriza a assinatura.
 * Recebe a composição de novo em vez de lê-la do tenant: o preço pode ter sido
 * renegociado entre provisionar e gerar o link, e o piso é revalidado aqui.
 */
export const CreateSaleCheckoutLinkInput = SaleLeadRefSchema.extend({
  tenantId: z.string().min(3).max(64),
  billingCycle: BillingCycleSchema,
  items: z.array(SaleItemSchema).min(1).max(30),
  /**
   * #752 (G2) — o gateway desta venda. Ausente = o padrão (o do parceiro
   * DIRECT, ou o do mercado). O servidor valida contra os permitidos
   * (`getSaleCheckoutOptions`); o parceiro não troca o do cadastro.
   *
   * Opcional para não quebrar cliente antigo do CRM (anterior à A3), que não
   * manda o campo: ele continua gerando o link pelo padrão. A recusa vem com
   * `details.reason` = `provider_not_allowed` | `provider_unavailable` |
   * `provider_locked_by_partner` (`resolveSaleGateway`).
   *
   * Origem: #752 — 2026-09-27 14:28 +0200 (A3: gateway por venda, G2).
   * Usado por: CRM `LeadCheckoutPage.tsx` (só o time interno manda; o parceiro
   * DIRECT e o fallback sem opções mandam ausente — #752, A3, F-M1/F-L4);
   * Functions `billing/createSaleCheckoutLink.ts`.
   */
  provider: PaymentProviderSchema.optional(),
});
export type CreateSaleCheckoutLinkInput = z.infer<typeof CreateSaleCheckoutLinkInput>;

export const CreateSaleCheckoutLinkOutput = z.object({
  url: z.string(),
  sessionId: z.string(),
  expiresAt: z.number().int().nullable(),
  totalCents: z.number().int().nonnegative(),
  currency: z.string(),
  /**
   * #752 — o gateway que de fato vai cobrar. Muda o significado dos campos
   * acima: no Stripe `url` é a Checkout Session e `sessionId` o `cs_…`; no
   * Asaas `url` é a NOSSA página `/{lang}/pay/{uuid}` e `sessionId` o id da
   * tentativa (`checkoutAttempts/{uuid}`) — nada é criado no Asaas até o
   * cliente escolher o meio. A Contratação o registra na telemetria
   * (`crm_checkout_link_generated`), porque o pedido pode ter ido sem gateway
   * (padrão do servidor) e só a resposta diz qual cobrou.
   *
   * Origem: #752 — 2026-09-27 14:28 +0200.
   * Dados (Asaas, anonimizado): `{ "url": "https://app.etiquetachef.com/pt/pay/00000000-0000-4000-8000-000000000000",
   * "sessionId": "00000000-0000-4000-8000-000000000000", "expiresAt": 1790600000000,
   * "totalCents": 24890, "currency": "BRL", "provider": "asaas" }`.
   */
  provider: PaymentProviderSchema,
});
export type CreateSaleCheckoutLinkOutput = z.infer<typeof CreateSaleCheckoutLinkOutput>;

/**
 * `getSaleCheckoutOptions` (#752): o que a Contratação pode oferecer neste
 * lead — os gateways permitidos, o padrão e, na venda de parceiro, o que o
 * cadastro dele fixa. É callable porque o parceiro não lê `crm_partners`
 * (rules), e porque "permitido" depende do tenant de teste, que só o
 * servidor sabe.
 *
 * Entrada: só o lead — o servidor deriva país, parceiro e tenant de teste dele.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200 (A3: gateway por venda, G2).
 *
 * Usado por: Functions `billing/getSaleCheckoutOptions.ts` (callable, codebase
 * principal); CRM `lib/callables.ts` (`getSaleCheckoutOptionsCallable`) →
 * `features/checkout/LeadCheckoutPage.tsx`.
 *
 * Dados: `{ "boardId": "board-1", "leadId": "lead-1" }`.
 */
export const GetSaleCheckoutOptionsInput = SaleLeadRefSchema;
export type GetSaleCheckoutOptionsInput = z.infer<typeof GetSaleCheckoutOptionsInput>;

/**
 * Saída de `getSaleCheckoutOptions`. A tela ANTECIPA com ela; quem decide é
 * `createSaleCheckoutLink`, pela mesma função pura (`saleGatewayOptions`), então
 * as duas respostas só divergem se o cadastro mudar entre uma chamada e outra.
 *
 * Origem: #752 — 2026-09-27 14:28 +0200.
 *
 * Usado por: Functions `billing/getSaleCheckoutOptions.ts` (produz); CRM
 * `features/checkout/LeadCheckoutPage.tsx` (modos da tela: time interno,
 * parceiro DIRECT, parceiro RESELLER).
 *
 * Dados — venda de parceiro DIRECT a 20 % pelo Asaas (anonimizado):
 *   { "allowedProviders": ["asaas"], "defaultProvider": "asaas",
 *     "partner": { "model": "DIRECT", "gateway": "asaas", "commissionPercent": 20 } }
 * Parceiro RESELLER no BR (#965): `{ "allowedProviders": ["asaas"], "defaultProvider": "asaas",
 * "partner": { "model": "RESELLER", "gateway": null, "commissionPercent": null } }`.
 * Time interno: `"partner": null`.
 *
 * Falhas conhecidas (auditoria da A3, #752):
 *  - 2026-09-27 15:48 +0200 (B-M5/F-L7): modelo e gateway eram lidos do
 *    schema tolerante (inválido → DIRECT/`null`); hoje o servidor confere o
 *    doc cru e devolve `partner_model_partner`/`partner_gateway_missing`.
 *  - 2026-09-27 15:49 +0200 (F-M1/F-L4): quando esta callable falhava, o
 *    parceiro caía no modo errado e o time interno ficava travado na janela de
 *    deploy; a tela passou a mostrar o motivo ao parceiro e a gerar pelo
 *    padrão para o time interno.
 *
 * Issues: #748 (G2/G3), #752 (A3), #756 (A7: o Asaas fora do tenant de teste).
 */
export const GetSaleCheckoutOptionsOutput = z.object({
  /**
   * Gateways que podem cobrar esta venda agora. Vazio quando nada pode: o
   * parceiro DIRECT cujo gateway o mercado/a chave não permitem (a tela
   * explica e não gera link). No RESELLER (#965) são os do mercado do país,
   * como na venda interna — o parceiro não escolhe gateway no cadastro.
   */
  allowedProviders: z.array(PaymentProviderSchema),
  /**
   * O pré-selecionado. Com parceiro DIRECT é o do cadastro, mesmo que não
   * esteja em `allowedProviders`; sem parceiro ou no RESELLER é o do mercado,
   * ou o 1º permitido.
   */
  defaultProvider: PaymentProviderSchema,
  /**
   * Por que `allowedProviders` veio vazio, quando o motivo não é do parceiro:
   * `market_in_sandbox` = o país está em modo de teste no admin e este lead
   * não é de um tenant de teste. Opcional para o CRM antigo/servidor antigo
   * conviverem. Origem: #756 — 2026-10-01 (auditoria A7).
   */
  blockedReason: z.enum(['market_in_sandbox']).nullable().optional(),
  /**
   * `null` = venda do time interno. Com parceiro, `gateway` é o único
   * permitido (decisão do owner, 2026-09-27) e a comissão é só leitura.
   */
  partner: z
    .object({
      /**
       * O modelo decide a tela: DIRECT = "Gerar link" com o gateway travado
       * e preço negociado acima do piso; RESELLER (#965) = "Gerar link" para o
       * PARCEIRO pagar, preço fixo da licença (`partner_license`) e só
       * mensal. Conferido pelo servidor no doc CRU — cadastro corrompido nunca
       * volta como DIRECT.
       */
      model: CrmPartnerCommercialModelSchema,
      /** O gateway do cadastro (DIRECT). `null` no RESELLER ou em cadastro sem gateway válido. */
      gateway: SplitGatewaySchema.nullable(),
      /**
       * O percentual do parceiro sobre o líquido, para a tela MOSTRAR (não
       * editar): o split é congelado no servidor a partir do cadastro.
       */
      commissionPercent: z.number().min(0).max(100).nullable(),
    })
    .nullable(),
});
export type GetSaleCheckoutOptionsOutput = z.infer<typeof GetSaleCheckoutOptionsOutput>;

/**
 * `getMyPendingCheckoutLink` (#679 §4.3-6): o "Pagar agora" da tela de espera
 * de pagamento (web e Android — o iOS não oferece pagamento, D16). Precisa de
 * callable porque as rules não deixam o cliente ler `checkoutAttempts`, e a
 * URL do checkout não fica no documento de assinatura.
 */
export const GetMyPendingCheckoutLinkInput = z.object({
  tenantId: z.string().min(3).max(64),
});
export type GetMyPendingCheckoutLinkInput = z.infer<typeof GetMyPendingCheckoutLinkInput>;

/**
 * Três estados, e não "url ou null": "já pagou" e "não há link vivo" pedem
 * telas diferentes — o primeiro some com a tela de espera (o listener do
 * documento chega logo), o segundo manda falar com o consultor.
 */
export const GetMyPendingCheckoutLinkOutput = z.discriminatedUnion('status', [
  z.object({ status: z.literal('PAID') }),
  z.object({ status: z.literal('LINK'), url: z.string() }),
  z.object({ status: z.literal('NO_LINK') }),
]);
export type GetMyPendingCheckoutLinkOutput = z.infer<typeof GetMyPendingCheckoutLinkOutput>;

/**
 * `adminListUnpaidSales` (#679 9.7): a fila "Vendas sem pagamento" do admin —
 * tenant da venda assistida sem pagamento há mais de 5 dias. A exclusão é
 * manual, pelo `DeleteTenant` do detalhe do tenant.
 */
export const AdminUnpaidSaleSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  createdAt: z.number().int().nonnegative(),
  sellerUid: z.string(),
  leadId: z.string(),
  boardId: z.string(),
  amountCents: z.number().int().nonnegative().nullable(),
  currency: z.string().nullable(),
});
export type AdminUnpaidSale = z.infer<typeof AdminUnpaidSaleSchema>;

export const AdminListUnpaidSalesOutput = z.object({
  sales: z.array(AdminUnpaidSaleSchema),
});
export type AdminListUnpaidSalesOutput = z.infer<typeof AdminListUnpaidSalesOutput>;

// ---------------------------------------------------------------------------
// F2 — escritor do piso e envio do link pelo colaborador
// ---------------------------------------------------------------------------

/**
 * `crmUpdatePricingFloors` (#679 F2, 9.1): o ADMIN do CRM substitui o piso
 * inteiro. As chaves continuam `string` no documento (esparso — ver
 * `PricingFloorsSchema`), mas a ESCRITA as valida: SKU pelo `SkuSchema`, moeda
 * e ciclo pelos enums. Uma chave errada no documento não quebra nada — ela só
 * nunca casa, e o SKU fica não vendável em silêncio; por isso é aqui, na porta,
 * que ela é recusada.
 *
 * Cada célula é só `{ min_cents }` (#964, 2026-10-13 18:14 +0200): o preço de
 * lista saiu. Um `list_cents` mandado por uma aba antiga do CRM é DESCARTADO
 * (strip padrão do `z.object`), não recusado — a aba aberta antes do deploy
 * continua salvando, e o documento sai limpo. Antes havia aqui a regra
 * `list_below_min`; não volta junto com a lista sem rever a #964.
 */
const PricingFloorWriteEntrySchema = z.object({ min_cents: CentsSchema });

export const CrmUpdatePricingFloorsInput = z
  .object({
    items: z.record(
      z.string(),
      z.record(z.string(), z.record(z.string(), PricingFloorWriteEntrySchema)),
    ),
    package: PricingFloorsSchema.shape.package.unwrap(),
    /**
     * #965 — licença do parceiro RESELLER (`PricingFloorsSchema.partner_license`).
     * AUSENTE = aba do CRM anterior à #965: o `crmUpdatePricingFloors` PRESERVA
     * o valor gravado, em vez de apagar a licença de todos os parceiros só
     * porque a tela antiga não conhecia a coluna.
     */
    partner_license: z.record(z.string(), z.record(z.string(), CentsSchema)).optional(),
  })
  .superRefine((floors, ctx) => {
    const currencies = new Set<string>(CurrencySchema.options);
    const cycles = new Set<string>(BillingCycleSchema.options);
    const checkKeys = (currency: string, cycle: string, path: (string | number)[]): void => {
      if (!currencies.has(currency)) {
        ctx.addIssue({ code: 'custom', message: `moeda inválida: ${currency}`, path });
      }
      if (!cycles.has(cycle)) {
        ctx.addIssue({ code: 'custom', message: `ciclo inválido: ${cycle}`, path });
      }
    };
    for (const [sku, byCurrency] of Object.entries(floors.items)) {
      if (!SkuSchema.safeParse(sku).success) {
        ctx.addIssue({ code: 'custom', message: `SKU inválido: ${sku}`, path: ['items', sku] });
      }
      for (const [currency, byCycle] of Object.entries(byCurrency)) {
        for (const cycle of Object.keys(byCycle)) {
          checkKeys(currency, cycle, ['items', sku, currency, cycle]);
        }
      }
    }
    for (const [currency, byCycle] of Object.entries(floors.package)) {
      for (const cycle of Object.keys(byCycle)) {
        checkKeys(currency, cycle, ['package', currency, cycle]);
      }
    }
    for (const [sku, byCurrency] of Object.entries(floors.partner_license ?? {})) {
      if (!SkuSchema.safeParse(sku).success) {
        ctx.addIssue({ code: 'custom', message: `SKU inválido: ${sku}`, path: ['partner_license', sku] });
      }
      for (const currency of Object.keys(byCurrency)) {
        if (!currencies.has(currency)) {
          ctx.addIssue({ code: 'custom', message: `moeda inválida: ${currency}`, path: ['partner_license', sku, currency] });
        }
      }
    }
  });
export type CrmUpdatePricingFloorsInput = z.infer<typeof CrmUpdatePricingFloorsInput>;

/** Quantas entradas de piso o documento tem (telemetria e audit). */
export function pricingFloorEntryCounts(floors: Pick<PricingFloors, 'items' | 'package'>): {
  itemEntries: number;
  packageEntries: number;
} {
  let itemEntries = 0;
  for (const byCurrency of Object.values(floors.items)) {
    for (const byCycle of Object.values(byCurrency)) itemEntries += Object.keys(byCycle).length;
  }
  let packageEntries = 0;
  for (const byCycle of Object.values(floors.package)) packageEntries += Object.keys(byCycle).length;
  return { itemEntries, packageEntries };
}

/**
 * `sendSaleCheckoutLinkEmail` (#679 F2): o colaborador reenvia o link de uma
 * venda sua ao OWNER por e-mail. É o `adminSendCheckoutLinkEmail` com o gate
 * do colaborador — a callable admin é super-admin e só aceita o CORS do admin.
 */
export const SendSaleCheckoutLinkEmailInput = SaleLeadRefSchema.extend({
  tenantId: z.string().min(3).max(64),
  sessionId: z.string().min(1).max(255),
});
export type SendSaleCheckoutLinkEmailInput = z.infer<typeof SendSaleCheckoutLinkEmailInput>;

export const SendSaleCheckoutLinkEmailOutput = z.object({
  email: z.string(),
  usedRecoveryUrl: z.boolean(),
});
export type SendSaleCheckoutLinkEmailOutput = z.infer<typeof SendSaleCheckoutLinkEmailOutput>;

/**
 * #743 — a composição da assinatura de um tenant de PARCEIRO, em função pura.
 *
 * Diferente da venda direta, os itens vendidos entram em `items[]` já na
 * ativação: não há webhook que os traga depois, porque não há gateway. Por
 * isso eles nascem `origin: 'PARTNER'` (sem ids, com `remit_cents`) e o
 * `capabilities` é derivado aqui, pela MESMA função do reconciler
 * (`computeCapabilities`) — o módulo vendido pelo parceiro vale na hora.
 *
 * O BASE vem primeiro (`SubscriptionSchema.items`: `items[0]` é sempre BASE),
 * os concedidos que a venda não cobra vêm depois.
 */
export function buildPartnerSaleSubscriptionFields(args: {
  readonly items: readonly PartnerSaleItemInput[];
  readonly grantItems: readonly SubscriptionItem[];
  readonly billingCycle: BillingCycle;
  readonly now: number;
}): {
  items: SubscriptionItem[];
  capabilities: string[];
  amount: number;
  total_amount_cents: number;
  total_remit_cents: number;
  commercial_mode: typeof CommercialMode.PARTNER;
  pricing_source: typeof PricingSource.PARTNER;
} {
  const ordered = [
    ...args.items.filter((it) => it.sku === Sku.BASE),
    ...args.items.filter((it) => it.sku !== Sku.BASE),
  ];
  const sold: SubscriptionItem[] = ordered.map((it) => ({
    sku: it.sku,
    external_item_id: null,
    external_price_id: null,
    amount_cents: it.amountCents,
    remit_cents: it.remitCents,
    billing_cycle: args.billingCycle,
    added_at: args.now,
    // Não há compra conosco, logo não há janela de arrependimento nossa.
    mbg_consumed: true,
    removed_at: null,
    pending_removal_at: null,
    gateway_removed_at: null,
    origin: SubscriptionItemOrigin.PARTNER,
  }));
  const soldSkus = new Set(sold.map((it) => it.sku));
  const items = [...sold, ...args.grantItems.filter((it) => !soldSkus.has(it.sku))];
  const total = sold.reduce((sum, it) => sum + it.amount_cents, 0);
  return {
    items,
    capabilities: computeCapabilities(items),
    amount: total,
    total_amount_cents: total,
    total_remit_cents: sold.reduce((sum, it) => sum + (it.remit_cents ?? 0), 0),
    commercial_mode: CommercialMode.PARTNER,
    pricing_source: PricingSource.PARTNER,
  };
}

/**
 * #743 (§4.5, B5) — pós-venda do PARCEIRO: a composição nova inteira (o que
 * ele cobra e repassa, item a item) aplicada sobre `items[]`, sem gateway.
 *
 * - SKU que continua: preço e repasse novos; a âncora (`added_at`) fica — é
 *   dela que o extrato conta os aniversários do ciclo (9.8);
 * - SKU que sai: fica até onde o cliente pagou — `pending_removal_at` no
 *   início do mês da próxima cobrança (`partnerItemPaidThrough`), com
 *   capability até lá; o passe 5 carimba `removed_at` na data. Nunca há
 *   reembolso (regra do owner, 2026-09-30, #755). Até a #755 saía na hora
 *   (`removed_at: now`) e o cliente perdia o que tinha pago;
 * - SKU que volta antes de sair: a saída é desfeita no MESMO item (preço e
 *   repasse novos), sem item novo;
 * - SKU novo: item PARTNER com âncora agora; um concedido do mesmo SKU é
 *   encerrado (dois itens do mesmo SKU seriam a #410);
 * - ciclo que muda: todo item de parceiro ganha âncora nova — o parceiro passa
 *   a cobrar o ciclo novo a partir de hoje.
 */
export function recomposePartnerSaleItems(args: {
  readonly current: readonly SubscriptionItem[];
  readonly items: readonly PartnerSaleItemInput[];
  readonly billingCycle: BillingCycle;
  readonly currentCycle: BillingCycle;
  readonly now: number;
}): SubscriptionItem[] {
  const next = new Map(args.items.map((it) => [it.sku, it]));
  const cycleChanged = args.billingCycle !== args.currentCycle;
  const seen = new Set<string>();
  const out: SubscriptionItem[] = args.current.map((it) => {
    if (it.removed_at != null) return it;
    if (it.origin === SubscriptionItemOrigin.PARTNER) {
      const wanted = next.get(it.sku);
      if (!wanted) {
        // #755 (2026-09-30 12:12 +0200): fica até onde pagou; saída já
        // agendada mantém a data (reenvio da mesma composição não a empurra).
        if (it.pending_removal_at != null) return it;
        return { ...it, pending_removal_at: partnerItemPaidThrough(it, args.now) };
      }
      seen.add(it.sku);
      return {
        ...it,
        amount_cents: wanted.amountCents,
        remit_cents: wanted.remitCents,
        billing_cycle: args.billingCycle,
        added_at: cycleChanged ? args.now : it.added_at,
        // Voltou antes de sair: o módulo continua, a saída agendada cai.
        pending_removal_at: null,
      };
    }
    if (it.origin === SubscriptionItemOrigin.GRANT && next.has(it.sku)) return { ...it, removed_at: args.now };
    return it;
  });
  for (const wanted of args.items) {
    if (seen.has(wanted.sku)) continue;
    out.push({
      sku: wanted.sku,
      external_item_id: null,
      external_price_id: null,
      amount_cents: wanted.amountCents,
      remit_cents: wanted.remitCents,
      billing_cycle: args.billingCycle,
      added_at: args.now,
      mbg_consumed: true,
      removed_at: null,
      pending_removal_at: null,
      gateway_removed_at: null,
      origin: SubscriptionItemOrigin.PARTNER,
    });
  }
  const base = out.filter((it) => it.sku === Sku.BASE && it.removed_at == null);
  const rest = out.filter((it) => !(it.sku === Sku.BASE && it.removed_at == null));
  return [...base, ...rest];
}

/**
 * Soma do repasse dos itens de parceiro que seguem no próximo ciclo. Item com
 * saída agendada (#755) não entra: ele não será mais cobrado.
 */
export function activeRemitCents(items: readonly SubscriptionItem[]): number {
  return items
    .filter((it) => it.origin === SubscriptionItemOrigin.PARTNER && it.removed_at == null && it.pending_removal_at == null)
    .reduce((sum, it) => sum + (it.remit_cents ?? 0), 0);
}
